/**
 * 90. Workflow Builder — visual state machine; conditions; approvals; SLA;
 * versioning; validation. Renders the workflow definition state/transition
 * graph from GET /v1/config/workflows/:id/versions/latest.
 *
 * Arabic and English (C7): every word from the catalogs; state names,
 * titles and events are the workflow's own definition and are shown as
 * defined. The state list indents on the inline-start side.
 */
import { useId } from "react";
import { Card, PageHeader, Badge, Skeleton } from "@/components/ui";
import { PendingButton, PendingNote } from "@/components/PendingAction";
import { useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";

interface Transition {
  from: string;
  to: string;
  event?: string;
  condition?: { exp: string };
  permission?: string;
}

interface WorkflowVersion {
  version: number;
  isActive: boolean;
  states: Record<
    string,
    { title: string; isInitial?: boolean; isFinal?: boolean }
  >;
  transitions: Transition[];
}

export function WorkflowBuilderPage() {
  const { t } = useTranslation();
  const pendingId = useId();
  const { data, isLoading } = useApiQuery<WorkflowVersion>(
    ["config", "workflow", "latest"],
    "/config/workflows/latest/versions/latest",
  );

  return (
    <div>
      <PageHeader
        title={t("admin.workflows.title")}
        subtitle={t("admin.workflows.subtitle")}
        actions={
          <>
            <PendingButton reasonId={pendingId}>
              {t("admin.workflows.validate")}
            </PendingButton>
            <PendingButton reasonId={pendingId}>
              {t("admin.workflows.simulate")}
            </PendingButton>
            <PendingButton variant="primary" reasonId={pendingId}>
              {t("admin.workflows.publish")}
            </PendingButton>
          </>
        }
      />
      <PendingNote id={pendingId} reason="afterLaunch" slice="F21" />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <div className="atlas-grid atlas-grid--2">
          <Card
            title={t("admin.workflows.states")}
            actions={
              <Badge tone={data.isActive ? "success" : "default"}>
                {t("admin.workflows.versionBadge", {
                  state: data.isActive
                    ? t("admin.workflows.active")
                    : t("admin.workflows.draft"),
                  version: data.version,
                })}
              </Badge>
            }
          >
            <ul style={{ margin: 0, paddingInlineStart: 18 }}>
              {Object.entries(data.states).map(([name, state]) => (
                <li key={name} style={{ marginBlockEnd: 8 }}>
                  <strong>{name}</strong>
                  {state.title !== name && (
                    <span className="atlas-muted"> — {state.title}</span>
                  )}
                  {state.isInitial && (
                    <Badge tone="primary" className="ml-2">
                      {t("admin.workflows.initial")}
                    </Badge>
                  )}
                  {state.isFinal && (
                    <Badge tone="success" className="ml-2">
                      {t("admin.workflows.final")}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </Card>
          <Card title={t("admin.workflows.transitions")}>
            <table className="atlas-table">
              <thead>
                <tr>
                  <th scope="col">{t("fields.from")}</th>
                  <th scope="col">{t("fields.to")}</th>
                  <th scope="col">{t("fields.event")}</th>
                </tr>
              </thead>
              <tbody>
                {data.transitions.map((tr, i) => (
                  <tr key={`${tr.from}-${tr.to}-${i}`}>
                    <td>{tr.from}</td>
                    <td>{tr.to}</td>
                    <td className="atlas-mono">
                      {tr.event ?? t("admin.workflows.auto")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}
